import { Typography, useTheme, Box, IconButton, LinearProgress, linearProgressClasses, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import FileUploadCta from "@hyro-components/file-upload/FileUploadCta";
import UploadIcon from "src/icons/UploadIcon";
import RemoveIcon from "src/icons/RemoveIcon";
import useFileUploadDialog from "../../hooks/FileUploadDialog.hook";
import { DEFAULT_TITLE, DEFAULT_CTA_TEXT, TEST_IDS, UPLOAD_SUCCESS, DRAG_DROP_CTA } from "./FileUploadDialog.consts";
import HyroDialog from "@hyro-components/Dialog";
import GreenCheckIcon from "src/icons/GreenCheckIcon";
import { PROGRESS, getSupportedTypesText, DEFAULT_ACCEPTED_TYPES } from "../../FileUpload.consts";
import { truncateFileName } from "../../FileUpload.utils";
import ErrorCircleIcon from "src/icons/ErrorCircleIcon";

const FileUploadDialog = ({
  open: isOpen,
  onSubmit,
  onClose,
  title = DEFAULT_TITLE,
  ctaText = DEFAULT_CTA_TEXT,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  multiple = true,
  fileToReplace = null,
  preUploadValidation
}) => {
  const {
    hover,
    setHover,
    uploadedFiles,
    fileProgress,
    isUploading,
    removeFile,
    getFileError,
    handleClose,
    handleSubmit,
    getRootProps,
    getInputProps,
    open,
    errors,
    duplicateFileError,
    hasFiles,
    uploadedCount,
    allFilesUploaded,
    filesToDisplay
  } = useFileUploadDialog({ acceptedFileTypes, onSubmit, onClose, multiple, fileToReplace, preUploadValidation });

  const theme = useTheme();

  return (
    <HyroDialog
      open={isOpen}
      handleClose={handleClose}
      title={title}
      maxWidth="sm"
      variant="transactional"
      handleCancel={handleClose}
      handleSubmit={() => handleSubmit(uploadedFiles)}
      cancelButtonText="Cancel"
      submitButtonText="Upload"
      disableSave={!hasFiles || isUploading || errors.length > 0 || (fileToReplace && uploadedFiles.length === 0)}
      testId={TEST_IDS.DIALOG}
      additionalButton={duplicateFileError ? (
        <Stack mr='auto' direction="row" alignItems="center" justifyContent='center' gap={.5} data-testid={TEST_IDS.DUPLICATE_FILE_ERROR}>
          <ErrorCircleIcon />
          <Typography variant="caption" color={theme.palette.error.main}>{duplicateFileError}</Typography>
        </Stack>
      ) : null}
    >
      <StyledBorder
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid={TEST_IDS.DROP_ZONE}
        {...getRootProps()}
      >
        <StyledTextContainer>
          <input {...getInputProps()} />
          <Stack>
            <UploadIcon stroke={hover ? theme.palette.primary[500] : theme.palette.text[800]} />
          </Stack>
          <Stack>
            <FileUploadCta
              ctaContent={DRAG_DROP_CTA}
              buttonContent={ctaText}
              buttonAction={open}
            />
            <Stack mt={.5}>
              {allFilesUploaded && uploadedFiles.length > 0 ? (
                <Stack direction="row" alignItems="center" justifyContent='center' gap={.5} data-testid={TEST_IDS.UPLOAD_SUCCESS}>
                  <GreenCheckIcon />
                  <Typography variant="caption" color={theme.palette.success[500]}>{UPLOAD_SUCCESS}</Typography>
                </Stack>
              ) : (
                <Typography variant="caption" textAlign="center" color="text.700">
                  {getSupportedTypesText(acceptedFileTypes)}
                </Typography>
              )}
            </Stack>
          </Stack>
        </StyledTextContainer>
      </StyledBorder>

      {hasFiles && <>
        <Typography variant="inputText2" component='div' sx={{ color: theme.palette.text[700], my: 2 }}>
          {uploadedCount}/{uploadedFiles.length || (fileToReplace ? 1 : 0)} files uploaded
        </Typography>
        <Stack
          gap={2}
          width="100%"
          maxHeight="235px"
          overflow="auto"
          data-testid={TEST_IDS.UPLOADED_FILES_LIST}
        >
          {filesToDisplay.map((file, index) => {
            const fileError = getFileError(file);
            const fileName = file.file_name;
            const fileType = file.file_type;
            const isReplacementFile = fileToReplace && file === fileToReplace;

            return <Box key={`${fileName}-${index}`}>
              <FileItem hasError={!!fileError}>
                <FileItemHeader>
                  <Typography variant="body2">{truncateFileName({ fileName, fileType })}</Typography>
                  {!isReplacementFile && <IconButton size='small' onClick={() => removeFile(file)} data-testid={TEST_IDS.REMOVE_FILE(index)}>
                    <RemoveIcon />
                  </IconButton>}
                </FileItemHeader>
                {!fileError && !isReplacementFile && fileProgress[fileName] !== PROGRESS.ENDED && (
                  <StyledLinearProgress variant="determinate" value={fileProgress[fileName] || PROGRESS.STARTED} data-testid={TEST_IDS.PROGRESS} />
                )}
              </FileItem>
              {fileError && <FileErrorMessage data-testid={TEST_IDS.FILE_ERROR(index)}>{fileError}</FileErrorMessage>}
            </Box>;
          })}
        </Stack>
      </>}
    </HyroDialog>
  );
};

const FileItem = styled(Box, {
  shouldForwardProp: prop => prop !== "hasError"
})(({ theme, hasError }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  padding: "8px 7px 8px 12px",
  border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.grey[300]}`,
  borderRadius: "8px",
  backgroundColor: theme.palette.system[0],
  width: "100%",
  height: "44px",
  overflow: "hidden"
}));

const FileItemHeader = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const FileErrorMessage = styled(Typography)(({ theme }) => ({
  ...theme.typography.helperText,
  color: theme.palette.error.main,
  marginTop: 4,
  marginLeft: 14
}));

const StyledTextContainer = styled(Stack)(() => ({
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  margin: "24px"
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 3,
  borderRadius: 10,
  width: "100%",
  position: "absolute",
  bottom: 0,
  left: 0,
  backgroundColor: "transparent",
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: theme.palette.primary[500]
  }
}));

const StyledBorder = styled("div", { shouldForwardProp: prop => prop !== "hasFiles" })(({ hasFiles, theme }) => ({
  display: "flex",
  borderWidth: 2,
  borderRadius: 8,
  backgroundColor: theme.palette.grey[50],
  borderColor: theme.palette.grey[300],
  borderStyle: "dashed",
  outline: "none",
  minHeight: "210px",
  width: "552px",
  transition: "0.1s",
  "&:hover": {
    backgroundColor: theme.palette.tertiary[300],
    borderColor: theme.palette.primary[500],
    transition: "0.2s"
  },
  ...(hasFiles && {
    border: `1px solid ${theme.palette.primary[500]}`
  })
}));

export default FileUploadDialog;
